import React, { useEffect, useRef, useState } from "react";
import { TenantLogo } from "@/components/TenantLogo";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

export default function VertragSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const padRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/contracts/sign/${token}`);
        setInfo(data);
        const pdf = await api.get(`/contracts/sign/${token}/pdf`, { responseType: "blob" });
        setPdfUrl(window.URL.createObjectURL(new Blob([pdf.data], { type: "application/pdf" })));
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async () => {
    if (!signature) { toast.error("Bitte unterschreiben"); return; }
    setSaving(true);
    setSubmitError(null);
    try {
      await api.post(`/contracts/sign/${token}`, { signature });
      setDone(true);
      toast.success("Vertrag unterschrieben");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setSubmitError(msg);
      toast.error(msg);
      if ((e.response?.data?.detail || "").includes("konnte nicht verarbeitet werden")) {
        padRef.current?.clear();
      }
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-xs text-slate-500">Lädt…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="max-w-md bg-white border border-slate-200 p-8">
        <div className="text-xs font-bold uppercase tracking-overline text-red-600 mb-2">Fehler</div>
        <div className="text-sm text-slate-700">{error}</div>
        <a href="/mein-bereich" className="text-xs text-[#0a788c] underline mt-4 inline-block">Zum Portal einloggen</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-2xl bg-white border border-slate-200" data-testid="vertrag-sign-page">
        <div className="px-8 py-6 border-b border-slate-100">
          <div className="mb-3"><TenantLogo className="w-40 h-auto" testId="sign-page-logo" /></div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-1">Teilnahmevertrag unterschreiben</div>
          <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">Hallo {info.client_name?.split(" ")[0] || info.client_name}.</div>
          <div className="text-xs text-slate-500 mt-1">Bitte prüfe deinen Vertrag unten und unterschreibe digital — kein Login nötig.</div>
        </div>

        <div className="px-8 py-6 space-y-4">
          {pdfUrl && (
            <iframe src={pdfUrl} title="Vertrag" className="w-full h-[420px] border border-slate-200" data-testid="vertrag-pdf-frame" />
          )}

          {done || info.already_signed ? (
            <div className="p-6 bg-emerald-50 border border-emerald-200 text-center" data-testid="vertrag-sign-done">
              <div className="text-2xl mb-2">✓</div>
              <div className="font-medium text-emerald-800">
                {done ? "Vertrag erfolgreich unterschrieben" : "Vertrag bereits unterschrieben"}
              </div>
              {info.client_signed_at && !done && (
                <div className="text-xs text-emerald-700 mt-1 font-mono">
                  {new Date(info.client_signed_at).toLocaleString("de-DE")}
                </div>
              )}
              <div className="text-xs text-emerald-700 mt-3">Vielen Dank! Wir freuen uns auf die Zusammenarbeit.</div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Deine Unterschrift</label>
                <SignaturePad ref={padRef} value={signature} onChange={setSignature} testId="vertrag-sign-pad" />
                <div className="text-[10px] text-slate-500 mt-2">Mit deiner Unterschrift bestätigst du die Teilnahmebedingungen des oben angezeigten Vertrags. Kein Login nötig.</div>
              </div>
              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 text-sm text-red-800" data-testid="vertrag-sign-error">
                  <b>Unterschrift konnte nicht gespeichert werden:</b> {submitError}
                  {submitError.includes("konnte nicht verarbeitet werden") && (
                    <div className="mt-1 text-xs text-red-700">Deine Unterschrift wurde zurückgesetzt — bitte zeichne sie unten noch einmal und versuche es erneut.</div>
                  )}
                </div>
              )}
              <button
                onClick={submit}
                disabled={saving || !signature}
                data-testid="vertrag-sign-submit"
                className="w-full bg-[#0a788c] text-white py-4 text-sm font-medium hover:bg-[#0a96b4] disabled:opacity-50"
              >
                {saving ? "Speichere…" : "Vertrag unterschreiben"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
